import { Link } from "react-router-dom";
import { BarChart3, LayoutGrid, List, Plus, Settings, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import ThemePicker from "@/components/RefClick/ThemePicker";
import LockChip from "@/components/RefClick/LockChip";
import OfflineChip from "@/components/RefClick/OfflineChip";
import SparkleBtn from "@/components/RefClick/SparkleBtn";
import descriptLogo from "@/assets/descript.png";

/**
 * Top bar of the floating panel.
 * Layout (left → right):
 *   Descript logo · wordmark        [OfflineChip · Stats · Settings ·
 *                                    Clear-month · Layout toggle · Theme ·
 *                                    Lock · Add]
 */
export default function Header({
  theme,
  onThemeChange,
  layout,
  onLayoutChange,
  onAdd,
  onOpenSettings,
  onOpenIpCheck,
  onCleared,
}) {
  // Compact icon-button styling shared by every tool in the right
  // cluster so they read as ONE grouped control strip instead of
  // seven floating chips. `iconBtn` is applied to every leaf so
  // sizing / border / hover states stay perfectly consistent.
  const iconBtn =
    "focus-accent inline-flex h-8 w-8 shrink-0 items-center justify-center " +
    "rounded-md text-text-muted transition-colors hover:bg-white/[0.06] " +
    "hover:text-accent";

  return (
    <header className="flex items-center justify-between gap-3 border-b border-white/5 px-3 py-3 sm:px-5 sm:py-3.5">
      <div className="flex items-center gap-2.5">
        <img
          src={descriptLogo}
          alt="Descript"
          width={26}
          height={26}
          data-testid="descript-logo"
          className="h-6 w-6 shrink-0 rounded-md bg-white/[0.04] object-contain p-1 ring-1 ring-white/10 sm:h-7 sm:w-7"
          style={{ boxShadow: "0 4px 14px -6px rgb(var(--accent) / 0.35)" }}
        />
        <div className="flex flex-col leading-tight">
          <span
            className="font-display text-lg font-bold tracking-tight text-text-main sm:text-xl"
            data-testid="app-wordmark"
          >
            RefClick
          </span>
          <span className="hidden text-[10px] uppercase tracking-[0.18em] text-text-muted/80 sm:block">
            Daily click tracker
          </span>
        </div>
      </div>

      {/* Grouped icon strip — one bordered pill hosts every tool so the
          cluster stays tidy on 320-px screens and doesn't wrap. The
          "primary" Add button lives outside on the far right to keep
          the CTA distinct. */}
      <div className="flex items-center gap-2">
        <OfflineChip />
        <div
          className="flex items-center gap-0.5 rounded-lg border border-white/10 bg-white/[0.03] p-0.5"
          data-testid="header-tool-cluster"
        >
          <SparkleBtn
            as={Link}
            to="/stats"
            data-testid="stats-nav-link"
            aria-label="Open stats"
            className={iconBtn}
          >
            <BarChart3 className="h-4 w-4" />
          </SparkleBtn>
          <SparkleBtn
            type="button"
            onClick={onOpenSettings}
            data-testid="settings-btn"
            aria-label="Open settings"
            className={iconBtn}
          >
            <Settings className="h-4 w-4" />
          </SparkleBtn>
          {/* IP Fraud Checker — compact square-glass widget. Keyless
              providers always active; env-key providers auto-activate
              when their key is set in backend/.env. */}
          <SparkleBtn
            type="button"
            onClick={onOpenIpCheck}
            data-testid="ipcheck-btn"
            aria-label="Open IP fraud checker"
            className={iconBtn}
          >
            <Shield className="h-4 w-4" />
          </SparkleBtn>
          {/* Quick-clear month button — Yes/No AlertDialog. Twin of the
              Settings → Danger zone (typed-CLEAR) flow. */}
          {/* HeaderClearMonthButton removed Feb-2026 per user request:
              "already available in Settings → Danger zone". Settings
              modal remains the single, gated entry point for the
              destructive month-wipe. */}
          <ToggleGroup
            type="single"
            value={layout}
            onValueChange={(v) => v && onLayoutChange(v)}
            className="hidden items-center gap-0.5 sm:flex"
            data-testid="layout-toggle"
          >
            <ToggleGroupItem
              value="list"
              aria-label="List view"
              data-testid="layout-toggle-list"
              className="h-8 w-8 rounded-md text-text-muted data-[state=on]:bg-accent/20 data-[state=on]:text-accent"
            >
              <List className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="grid"
              aria-label="Grid view"
              data-testid="layout-toggle-grid"
              className="h-8 w-8 rounded-md text-text-muted data-[state=on]:bg-accent/20 data-[state=on]:text-accent"
            >
              <LayoutGrid className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <ThemePicker value={theme} onChange={onThemeChange} compact />
          <LockChip />
        </div>

        <SparkleBtn
          as={Button}
          type="button"
          onClick={onAdd}
          data-testid="add-link-btn"
          variant="star"
          className="h-8 gap-1.5 rounded-lg border border-accent/40 bg-accent/15 px-2.5 text-accent hover:bg-accent/25 focus-accent sm:h-9 sm:px-3"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden text-sm sm:inline">Add link</span>
        </SparkleBtn>
      </div>
    </header>
  );
}
